import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  postingLogApi,
  NO_ERP_INTEGRATION_MESSAGE,
  type CostingIntegrationStatus,
  type PagedCostingTransactionLogResponse,
} from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { PostingLogQuery } from '@/utils/PostingLog/types';
import { PAGE_SIZE } from '@/utils/PostingLog/constants';
import { mapPostingLogRow } from '@/utils/PostingLog/mappers';

interface UsePostingLogDataParams {
  canView: boolean;
  query: PostingLogQuery;
  page: number;
  matchesNothing: boolean;
  companyId: string | undefined;
}

export function usePostingLogData({ canView, query, page, matchesNothing, companyId }: UsePostingLogDataParams) {
  const [result, setResult] = useState<PagedCostingTransactionLogResponse | null>(null);
  const [integration, setIntegration] = useState<CostingIntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!canView || matchesNothing) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    postingLogApi
      .getPostingLog({ ...query, pageNumber: page, pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setIntegration(response.integration ?? null);
        setAccessDenied(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setAccessDenied(true);
        else setLoadError(err instanceof Error ? err.message : 'Failed to load the posting log');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [canView, query, page, refreshKey, companyId]);

  const rows = useMemo(
    () => (matchesNothing ? [] : (result?.items ?? []).map(mapPostingLogRow)),
    [result, matchesNothing],
  );
  const totalCount = matchesNothing ? 0 : result?.totalCount ?? 0;

  const integrationConfigured = integration?.isConfigured !== false;
  const retryDisabledReason = integrationConfigured ? undefined : NO_ERP_INTEGRATION_MESSAGE;
  const disabledRetryIds = useMemo(
    () => new Map(retryDisabledReason ? rows.map((r) => [r.id, retryDisabledReason] as const) : []),
    [rows, retryDisabledReason],
  );

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return {
    result, rows, totalCount, isLoading, loadError, accessDenied, refreshKey, refresh,
    integrationConfigured, retryDisabledReason, disabledRetryIds,
  };
}
